import React, { useMemo } from "react";
import { Badge, Box, Card, Group, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Printable, usePrintableContext } from "@components";
import { AntiSaccadeSample, AntiSaccadeTrial, AntiSaccadesTestResult } from "types/testTypes";

const TR = "AntiSaccadesResult";

const COLOR_CORRECT = "#2f9e44";
const COLOR_ERROR = "#e03131";
const COLOR_CORRECTED = "#d9480f";
const COLOR_INVALID = "#adb5bd";
const COLOR_STIMULUS = "#495057";
const COLOR_ANTITARGET = "#4dabf7";
const COLOR_BORDERLINE_BG = "#fff3bf";
const COLOR_BORDERLINE_BORDER = "#fab005";

const SACCADE_THRESHOLD_DEG = 5;
const SACCADE_SEARCH_WINDOW_S = 1.5;
const CORRECTION_WINDOW_S = 0.5;
// In the bordered report (prettier) layout each Printable.Section is one A4
// page; 12 trace panels (3 cols × 4 rows) fit a page with room for the logo.
const TRACE_PANELS_PER_PRINT_PAGE = 12;
const EXPRESS_LATENCY_MS = 200;
const VALID_LATENCY_MIN_MS = 80;
const VALID_LATENCY_MAX_MS = 800;

// Placeholder normative latency reference until an internal database is wired in.
// Healthy young adult correct anti-saccade latency ≈ 280 ± 60 ms.
const NORM_LATENCY_MEAN_MS = 280;
const NORM_LATENCY_SD_MS = 60;

// Z-score band colors for the "latency vs norm" chips.
const ZBAND_FAST = { bg: "#e5dbff", fg: "#5f3dc4", labelKey: `${TR}.zBand.fast` };
const ZBAND_TYPICAL = { bg: "#d3f9d8", fg: "#2b8a3e", labelKey: `${TR}.zBand.typical` };
const ZBAND_MILD = { bg: "#fff4d6", fg: "#a65500", labelKey: `${TR}.zBand.mild` };
const ZBAND_MODERATE = { bg: "#ffe8cc", fg: "#d9480f", labelKey: `${TR}.zBand.moderate` };
const ZBAND_HIGH = { bg: "#ffe3e3", fg: "#c92a2a", labelKey: `${TR}.zBand.high` };

function latencyZScore(latencyMs: number): number {
  return (latencyMs - NORM_LATENCY_MEAN_MS) / NORM_LATENCY_SD_MS;
}

function latencyZBand(latencyMs: number | null) {
  if (latencyMs == null) return null;
  const z = latencyZScore(latencyMs);
  if (z < -0.5) return ZBAND_FAST;
  if (Math.abs(z) < 0.5) return ZBAND_TYPICAL;
  if (z < 1.5) return ZBAND_MILD;
  if (z < 2.5) return ZBAND_MODERATE;
  return ZBAND_HIGH;
}

// ─── numeric helpers ────────────────────────────────────────────────────────

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const m = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[m - 1] + sorted[m]) / 2 : sorted[m];
}

function mean(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function stdDev(values: number[]): number | null {
  if (values.length < 2) return null;
  const m = mean(values)!;
  const variance = values.reduce((sum, v) => sum + (v - m) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

function wilsonCI(successes: number, total: number, z = 1.96): [number, number] | null {
  if (total <= 0) return null;
  const p = successes / total;
  const denom = 1 + (z * z) / total;
  const center = (p + (z * z) / (2 * total)) / denom;
  const margin = (z * Math.sqrt((p * (1 - p)) / total + (z * z) / (4 * total * total))) / denom;
  return [Math.max(0, center - margin), Math.min(1, center + margin)];
}

// ─── data derivation ────────────────────────────────────────────────────────

function combinedHorizontalGaze(s: AntiSaccadeSample): number | null {
  const lx = s.leftGazeAngles?.x;
  const rx = s.rightGazeAngles?.x;
  const validL = typeof lx === "number" && Number.isFinite(lx);
  const validR = typeof rx === "number" && Number.isFinite(rx);
  if (validL && validR) return (lx + rx) / 2;
  if (validL) return lx;
  if (validR) return rx;
  return null;
}

function indexSamplesByTrial(samples: AntiSaccadeSample[]) {
  const map = new Map<number, AntiSaccadeSample[]>();
  for (const s of samples) {
    if (s.trialIndex == null) continue;
    let arr = map.get(s.trialIndex);
    if (!arr) {
      arr = [];
      map.set(s.trialIndex, arr);
    }
    arr.push(s);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => a.timeStamp - b.timeStamp);
  }
  return map;
}

interface EnrichedTrial {
  trial: AntiSaccadeTrial;
  peripheralAngleDeg: number;
  antiTargetAngleDeg: number;
  peripheralShownAt: number | null;
  primaryAngleDeg: number | null;
  finalAngleDeg: number | null;
  latencyMs: number | null;
  outcome: "correct" | "error_corrected" | "error_uncorrected" | "invalid";
  isExpress: boolean;
  amplitudeGain: number | null;
}

function deriveEnrichedTrial(
  trial: AntiSaccadeTrial,
  samplesByTrial: Map<number, AntiSaccadeSample[]>,
): EnrichedTrial {
  const peripheralAngleDeg =
    trial.peripheralAngleDeg ??
    trial.peripheralHorizontalDegrees ??
    (trial.peripheralSide === "right" ? 1 : -1);
  const antiTargetAngleDeg =
    trial.antisaccadeAngleDeg ?? trial.mirrorHorizontalDegrees ?? -peripheralAngleDeg;
  const peripheralShownAt = trial.peripheralShownAt ?? null;
  const peripheralSign = Math.sign(peripheralAngleDeg) || 1;

  const trialSamples = samplesByTrial.get(trial.trialIndex) ?? [];

  // Use headset-provided fields if present, but always try to detect correction.
  let primaryAngleDeg: number | null = trial.firstSaccadeAngleDeg ?? null;
  let latencyMs: number | null = trial.latencyMs ?? trial.reactionTimeMs ?? null;
  let firstSaccadeIdx = -1;

  if (peripheralShownAt != null && trialSamples.length) {
    const deadline = peripheralShownAt + SACCADE_SEARCH_WINDOW_S;
    for (let i = 0; i < trialSamples.length; i++) {
      const s = trialSamples[i];
      if (s.timeStamp < peripheralShownAt) continue;
      if (s.timeStamp > deadline) break;
      const gx = combinedHorizontalGaze(s);
      if (gx == null) continue;
      if (Math.abs(gx) < SACCADE_THRESHOLD_DEG) continue;
      firstSaccadeIdx = i;
      if (primaryAngleDeg == null) primaryAngleDeg = gx;
      if (latencyMs == null) latencyMs = Math.round((s.timeStamp - peripheralShownAt) * 1000);
      break;
    }
  }

  if (primaryAngleDeg == null) {
    return {
      trial,
      peripheralAngleDeg,
      antiTargetAngleDeg,
      peripheralShownAt,
      primaryAngleDeg: null,
      finalAngleDeg: null,
      latencyMs,
      outcome: "invalid",
      isExpress: false,
      amplitudeGain: null,
    };
  }

  const primarySign = Math.sign(primaryAngleDeg);
  const isCorrect = primarySign !== peripheralSign;

  let finalAngleDeg: number | null = null;
  if (!isCorrect && firstSaccadeIdx >= 0) {
    const correctionDeadline = trialSamples[firstSaccadeIdx].timeStamp + CORRECTION_WINDOW_S;
    for (let j = firstSaccadeIdx + 1; j < trialSamples.length; j++) {
      const s = trialSamples[j];
      if (s.timeStamp > correctionDeadline) break;
      const gx = combinedHorizontalGaze(s);
      if (gx == null) continue;
      if (Math.sign(gx) !== peripheralSign && Math.abs(gx) >= SACCADE_THRESHOLD_DEG) {
        finalAngleDeg = gx;
        break;
      }
    }
  }

  let outcome: EnrichedTrial["outcome"];
  if (isCorrect) outcome = "correct";
  else if (finalAngleDeg != null) outcome = "error_corrected";
  else outcome = "error_uncorrected";

  const mirrorAmplitude = Math.abs(antiTargetAngleDeg);
  const amplitudeGain =
    isCorrect && mirrorAmplitude > 0 ? Math.abs(primaryAngleDeg) / mirrorAmplitude : null;

  const isExpress =
    isCorrect && latencyMs != null && latencyMs < EXPRESS_LATENCY_MS && latencyMs > 0;

  return {
    trial,
    peripheralAngleDeg,
    antiTargetAngleDeg,
    peripheralShownAt,
    primaryAngleDeg,
    finalAngleDeg,
    latencyMs,
    outcome,
    isExpress,
    amplitudeGain,
  };
}

// ─── stats summary ──────────────────────────────────────────────────────────

interface SummaryStats {
  trialsPresented: number;
  validTrials: number;
  correct: number;
  errorsCorrected: number;
  errorsUncorrected: number;
  expressSaccades: number;
  errorRate: number | null;
  errorRateCI: [number, number] | null;
  meanLatency: number | null;
  medianLatency: number | null;
  latencySd: number | null;
  leftMeanLatency: number | null;
  rightMeanLatency: number | null;
  directionalAsymmetry: number | null;
  correctionRate: number | null;
  meanAmplitudeGain: number | null;
}

function computeSummary(enriched: EnrichedTrial[]): SummaryStats {
  const trialsPresented = enriched.length;
  const valid = enriched.filter((e) => e.outcome !== "invalid");
  const validTrials = valid.length;
  const correct = enriched.filter((e) => e.outcome === "correct").length;
  const errorsCorrected = enriched.filter((e) => e.outcome === "error_corrected").length;
  const errorsUncorrected = enriched.filter((e) => e.outcome === "error_uncorrected").length;
  const totalErrors = errorsCorrected + errorsUncorrected;
  const expressSaccades = enriched.filter((e) => e.isExpress).length;

  const correctLatencies = enriched
    .filter((e) => e.outcome === "correct" && e.latencyMs != null)
    .map((e) => e.latencyMs!) as number[];
  const leftCorrectLatencies = enriched
    .filter(
      (e) => e.outcome === "correct" && e.trial.peripheralSide === "right" && e.latencyMs != null,
    )
    .map((e) => e.latencyMs!) as number[];
  const rightCorrectLatencies = enriched
    .filter(
      (e) => e.outcome === "correct" && e.trial.peripheralSide === "left" && e.latencyMs != null,
    )
    .map((e) => e.latencyMs!) as number[];
  // Directional asymmetry naming: leftward saccade = stimulus on right (peripheralSide right);
  // rightward saccade = stimulus on left (peripheralSide left).

  const errorRate = validTrials ? totalErrors / validTrials : null;
  const meanLatencyRaw = mean(correctLatencies);
  const meanLatency = meanLatencyRaw != null ? Math.round(meanLatencyRaw) : null;
  const medianLatencyRaw = median(correctLatencies);
  const medianLatency = medianLatencyRaw != null ? Math.round(medianLatencyRaw) : null;
  const sdRaw = stdDev(correctLatencies);
  const latencySd = sdRaw != null ? Math.round(sdRaw) : null;
  const leftMeanRaw = mean(leftCorrectLatencies);
  const rightMeanRaw = mean(rightCorrectLatencies);
  const leftMeanLatency = leftMeanRaw != null ? Math.round(leftMeanRaw) : null;
  const rightMeanLatency = rightMeanRaw != null ? Math.round(rightMeanRaw) : null;
  const directionalAsymmetry =
    leftMeanLatency != null && rightMeanLatency != null
      ? Math.abs(leftMeanLatency - rightMeanLatency)
      : null;
  const correctionRate = totalErrors > 0 ? errorsCorrected / totalErrors : null;
  const amplitudeGains = enriched.map((e) => e.amplitudeGain).filter((v): v is number => v != null);
  const meanAmplitudeGain = mean(amplitudeGains);

  return {
    trialsPresented,
    validTrials,
    correct,
    errorsCorrected,
    errorsUncorrected,
    expressSaccades,
    errorRate,
    errorRateCI: errorRate != null ? wilsonCI(totalErrors, validTrials) : null,
    meanLatency,
    medianLatency,
    latencySd,
    leftMeanLatency,
    rightMeanLatency,
    directionalAsymmetry,
    correctionRate,
    meanAmplitudeGain,
  };
}

// ─── result interpretation ──────────────────────────────────────────────────

type I18nFragment = { key: string; params?: Record<string, string | number> };

interface Interpretation {
  riskLabel: "normal" | "borderline" | "review";
  headline: I18nFragment;
  description: I18nFragment[];
}

function interpret(stats: SummaryStats): Interpretation {
  const errorRatePct = (stats.errorRate ?? 0) * 100;
  const allErrorsCorrected = stats.errorsUncorrected === 0 && stats.errorsCorrected > 0;

  let riskLabel: Interpretation["riskLabel"];
  if (errorRatePct < 15 && stats.errorsUncorrected === 0) riskLabel = "normal";
  else if (errorRatePct < 35) riskLabel = "borderline";
  else riskLabel = "review";

  const totalErrors = stats.errorsCorrected + stats.errorsUncorrected;
  const errorRateStr = errorRatePct.toFixed(0);
  let headline: I18nFragment;
  if (totalErrors === 0) {
    headline = { key: `${TR}.headline.noErrors`, params: { errorRate: errorRateStr } };
  } else if (allErrorsCorrected) {
    headline = { key: `${TR}.headline.allCorrected`, params: { errorRate: errorRateStr } };
  } else {
    headline = {
      key: `${TR}.headline.uncorrected`,
      params: { errorRate: errorRateStr, count: stats.errorsUncorrected },
    };
  }

  const description: I18nFragment[] = [
    {
      key: `${TR}.description.intro`,
      params: { correct: stats.correct, validTrials: stats.validTrials },
    },
  ];
  if (totalErrors > 0) {
    description.push({
      key: `${TR}.description.errorsToward`,
      params: { count: totalErrors },
    });
    description.push({
      key:
        stats.errorsCorrected > 0
          ? `${TR}.description.errorsCorrectedTail`
          : `${TR}.description.errorsPeriod`,
    });
  }
  if (stats.meanLatency != null) {
    description.push({
      key: `${TR}.description.meanLatency`,
      params: { value: stats.meanLatency },
    });
  }

  return { riskLabel, headline, description };
}

// ─── visualization: trial-by-trial chart ────────────────────────────────────

function outcomeColor(outcome: EnrichedTrial["outcome"]): string {
  switch (outcome) {
    case "correct":
      return COLOR_CORRECT;
    case "error_corrected":
      return COLOR_CORRECTED;
    case "error_uncorrected":
      return COLOR_ERROR;
    default:
      return COLOR_INVALID;
  }
}

function TrialByTrialChart({ enriched }: { enriched: EnrichedTrial[] }) {
  const { t } = useTranslation();
  if (!enriched.length) return null;
  const PAD_TOP = 30;
  const PAD_BOTTOM = 24;
  const TRIAL_LABEL_W = 42;
  const SIDE_LABEL_W = 22;
  const PAD_LEFT = TRIAL_LABEL_W + SIDE_LABEL_W;
  const PAD_RIGHT = 110;
  const ROW_H = 26;
  const PLOT_W = 720;
  const totalH = PAD_TOP + enriched.length * ROW_H + PAD_BOTTOM;
  const plotLeft = PAD_LEFT;
  const plotRight = PAD_LEFT + PLOT_W;
  const xMin = -32;
  const xMax = 32;
  const xScale = (deg: number) =>
    plotLeft + ((deg - xMin) / (xMax - xMin)) * (plotRight - plotLeft);
  const xTicks = [-30, -15, 0, 15, 30];
  const xGridDeg = Array.from({ length: 13 }, (_, i) => -30 + i * 5);

  const latencyChipW = 60;
  const latencyChipH = 16;

  return (
    <svg
      viewBox={`0 0 ${PAD_LEFT + PLOT_W + PAD_RIGHT} ${totalH}`}
      style={{ width: "100%", display: "block" }}
    >
      {/* header row */}
      <text x={TRIAL_LABEL_W - 4} y={12} fontSize={11} fill="#666" textAnchor="end">
        {t(`${TR}.trialByTrial.headerTrial`)}
      </text>
      <text x={TRIAL_LABEL_W + 4} y={12} fontSize={11} fill="#666" textAnchor="start">
        {t(`${TR}.trialByTrial.headerSide`)}
      </text>
      <text x={PAD_LEFT + PLOT_W / 2} y={12} fontSize={11} fill="#666" textAnchor="middle">
        {t(`${TR}.trialByTrial.headerXAxis`)}
      </text>
      <text x={PAD_LEFT + PLOT_W + 8} y={12} fontSize={11} fill="#666" textAnchor="start">
        {t(`${TR}.trialByTrial.headerLatencyNorm`)}
      </text>

      {/* x grid: dashed line every 5° */}
      {xGridDeg.map((deg) =>
        deg === 0 ? null : (
          <line
            key={`grid-${deg}`}
            x1={xScale(deg)}
            x2={xScale(deg)}
            y1={PAD_TOP - 6}
            y2={totalH - PAD_BOTTOM + 4}
            stroke="#e9ecef"
            strokeWidth={1}
            strokeDasharray="2 3"
          />
        ),
      )}

      {/* center line emphasis */}
      <line
        x1={xScale(0)}
        x2={xScale(0)}
        y1={PAD_TOP - 6}
        y2={totalH - PAD_BOTTOM + 4}
        stroke="#dee2e6"
        strokeWidth={1.5}
      />

      {/* x labels */}
      {xTicks.map((tick) => (
        <text
          key={`xlbl-${tick}`}
          x={xScale(tick)}
          y={totalH - PAD_BOTTOM + 16}
          fontSize={10}
          fill="#868e96"
          textAnchor="middle"
        >
          {tick > 0 ? `+${tick}°` : `${tick}°`}
        </text>
      ))}

      {/* rows */}
      {enriched.map((e, i) => {
        const y = PAD_TOP + i * ROW_H + ROW_H / 2;
        const trialLabel = `T${e.trial.trialIndex + 1}`;
        const sideLabel = e.trial.peripheralSide === "right" ? "R" : "L";
        const stimX = xScale(e.peripheralAngleDeg);
        const antiX = xScale(e.antiTargetAngleDeg);
        const primaryX = e.primaryAngleDeg != null ? xScale(e.primaryAngleDeg) : null;
        const finalX = e.finalAngleDeg != null ? xScale(e.finalAngleDeg) : null;
        const color = outcomeColor(e.outcome);
        const band = latencyZBand(e.latencyMs);
        const latencyText = e.latencyMs != null ? `${e.latencyMs} ms` : t(`${TR}.noData`);
        const chipX = PAD_LEFT + PLOT_W + 8;
        const chipY = y - latencyChipH / 2;

        return (
          <g key={trialLabel}>
            <text x={TRIAL_LABEL_W - 4} y={y + 4} fontSize={10} fill="#495057" textAnchor="end">
              {trialLabel}
            </text>
            <text
              x={TRIAL_LABEL_W + SIDE_LABEL_W - 4}
              y={y + 4}
              fontSize={10}
              fill="#868e96"
              textAnchor="end"
              fontWeight={600}
            >
              {sideLabel}
            </text>

            {/* stimulus marker (open square) */}
            <rect
              x={stimX - 5}
              y={y - 5}
              width={10}
              height={10}
              fill="none"
              stroke={COLOR_STIMULUS}
              strokeWidth={1}
            />

            {/* anti-target marker (blue triangle) */}
            <polygon
              points={`${antiX},${y - 6} ${antiX - 5},${y + 4} ${antiX + 5},${y + 4}`}
              fill={COLOR_ANTITARGET}
              opacity={0.85}
            />

            {/* correction line */}
            {e.outcome === "error_corrected" && primaryX != null && finalX != null && (
              <line
                x1={primaryX}
                y1={y}
                x2={finalX}
                y2={y}
                stroke={COLOR_CORRECTED}
                strokeWidth={1}
                strokeDasharray="3 2"
              />
            )}

            {/* primary endpoint */}
            {primaryX != null && (
              <circle
                cx={primaryX}
                cy={y}
                r={4.5}
                fill={e.outcome === "error_corrected" ? COLOR_CORRECTED : color}
                stroke={e.outcome === "error_corrected" ? "#fff" : "none"}
                strokeWidth={1}
              />
            )}

            {/* final (corrected) endpoint */}
            {e.outcome === "error_corrected" && finalX != null && (
              <circle
                cx={finalX}
                cy={y}
                r={4.5}
                fill="#fff"
                stroke={COLOR_CORRECTED}
                strokeWidth={1.5}
              />
            )}

            {/* latency-vs-norm chip */}
            {band ? (
              <>
                <rect
                  x={chipX}
                  y={chipY}
                  width={latencyChipW}
                  height={latencyChipH}
                  rx={3}
                  fill={band.bg}
                />
                <text
                  x={chipX + latencyChipW / 2}
                  y={y + 4}
                  fontSize={10}
                  fill={band.fg}
                  textAnchor="middle"
                  fontWeight={600}
                >
                  {latencyText}
                </text>
              </>
            ) : (
              <text x={chipX} y={y + 4} fontSize={10} fill="#868e96" textAnchor="start">
                {latencyText}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── visualization: endpoint distribution ───────────────────────────────────

function EndpointDistribution({ enriched }: { enriched: EnrichedTrial[] }) {
  const { t } = useTranslation();
  const valid = enriched.filter((e) => e.primaryAngleDeg != null);
  if (!valid.length) return null;
  // Geometry kept identical to LatencyHistogram so the two side-by-side plots' x-labels align.
  const W = 380;
  const H = 180;
  const PAD_X = 36;
  const PAD_TOP = 36;
  const PAD_BOTTOM = 50;
  const xMin = -32;
  const xMax = 32;
  const xScale = (deg: number) => PAD_X + ((deg - xMin) / (xMax - xMin)) * (W - 2 * PAD_X);
  const xTicks = [-30, -20, -10, 0, 10, 20, 30];
  const baselineY = PAD_TOP + (H - PAD_TOP - PAD_BOTTOM) / 2;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
      <text x={PAD_X} y={14} fontSize={10} fill="#868e96" textAnchor="start">
        {t(`${TR}.endpoints.leftSide`)}
      </text>
      <text x={W - PAD_X} y={14} fontSize={10} fill="#868e96" textAnchor="end">
        {t(`${TR}.endpoints.rightSide`)}
      </text>
      <line
        x1={xScale(0)}
        x2={xScale(0)}
        y1={PAD_TOP - 4}
        y2={H - PAD_BOTTOM + 4}
        stroke="#dee2e6"
        strokeDasharray="2 2"
      />
      <line x1={PAD_X} x2={W - PAD_X} y1={baselineY} y2={baselineY} stroke="#e9ecef" />
      {/* points */}
      {valid.map((e, i) => {
        const jitterY = ((i % 5) - 2) * 4;
        const cx = xScale(e.primaryAngleDeg!);
        const cy = baselineY + jitterY;
        const color = outcomeColor(e.outcome);
        if (e.outcome === "error_corrected") {
          const finalX = e.finalAngleDeg != null ? xScale(e.finalAngleDeg) : null;
          return (
            <g key={`pt-${e.trial.trialIndex}`}>
              {finalX != null && (
                <line
                  x1={cx}
                  y1={cy}
                  x2={finalX}
                  y2={cy}
                  stroke={COLOR_CORRECTED}
                  strokeDasharray="2 2"
                />
              )}
              <circle cx={cx} cy={cy} r={3.5} fill="none" stroke={COLOR_CORRECTED} />
              {finalX != null && <circle cx={finalX} cy={cy} r={3.5} fill={COLOR_CORRECTED} />}
            </g>
          );
        }
        return (
          <circle
            key={`pt-${e.trial.trialIndex}`}
            cx={cx}
            cy={cy}
            r={3.5}
            fill={color}
            opacity={0.85}
          />
        );
      })}
      {/* x labels */}
      {xTicks.map((tick) => (
        <g key={`xlbl-${tick}`}>
          <line
            x1={xScale(tick)}
            x2={xScale(tick)}
            y1={H - PAD_BOTTOM}
            y2={H - PAD_BOTTOM + 3}
            stroke="#868e96"
          />
          <text
            x={xScale(tick)}
            y={H - PAD_BOTTOM + 14}
            fontSize={10}
            fill="#868e96"
            textAnchor="middle"
          >
            {tick > 0 ? `+${tick}°` : `${tick}°`}
          </text>
        </g>
      ))}
      <text x={W / 2} y={H - PAD_BOTTOM + 30} fontSize={11} fill="#495057" textAnchor="middle">
        {t(`${TR}.endpoints.xAxis`)}
      </text>
    </svg>
  );
}

// ─── visualization: latency histogram ──────────────────────────────────────

function LatencyHistogram({ enriched }: { enriched: EnrichedTrial[] }) {
  const { t } = useTranslation();
  const correct = enriched.filter((e) => e.outcome === "correct" && e.latencyMs != null);
  if (!correct.length) return null;

  const binSize = 25;
  const minBin = VALID_LATENCY_MIN_MS;
  const maxBin = VALID_LATENCY_MAX_MS;
  const bins: { label: number; left: number; right: number }[] = [];
  for (let lo = minBin; lo < maxBin; lo += binSize) {
    bins.push({ label: lo, left: 0, right: 0 });
  }
  let leftCount = 0;
  let rightCount = 0;
  correct.forEach((e) => {
    const lat = e.latencyMs!;
    if (lat < minBin || lat >= maxBin) return;
    const idx = Math.floor((lat - minBin) / binSize);
    if (e.trial.peripheralSide === "right") {
      bins[idx].left += 1;
      leftCount += 1;
    } else {
      bins[idx].right += 1;
      rightCount += 1;
    }
  });
  const maxBarVal = Math.max(1, ...bins.map((b) => b.left + b.right));
  const yMax = maxBarVal + 1;

  const W = 380;
  const H = 180;
  const PAD_X = 36;
  const PAD_TOP = 12;
  const PAD_BOTTOM = 50;
  const plotW = W - 2 * PAD_X;
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const xScale = (ms: number) => PAD_X + (ms / maxBin) * plotW;
  const barW = (binSize / maxBin) * plotW;
  const yScale = (v: number) => PAD_TOP + plotH - (v / yMax) * plotH;

  const yTicks = Array.from({ length: yMax + 1 }, (_, i) => i).filter(
    (v) => v === yMax || v <= 5 || v % Math.ceil(yMax / 5) === 0,
  );

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
      {yTicks.map((tick) => (
        <g key={`y-${tick}`}>
          <line x1={PAD_X} x2={W - PAD_X} y1={yScale(tick)} y2={yScale(tick)} stroke="#f1f3f5" />
          <text x={PAD_X - 6} y={yScale(tick) + 3} fontSize={10} fill="#868e96" textAnchor="end">
            {tick}
          </text>
        </g>
      ))}
      {bins.map((b, i) => {
        const x = xScale(b.label) + 1;
        const w = Math.max(1, barW - 2);
        const leftH = (b.left / yMax) * plotH;
        const rightH = (b.right / yMax) * plotH;
        const leftY = yScale(b.left);
        const rightY = yScale(b.left + b.right);
        return (
          <g key={`bar-${i}`}>
            {b.left > 0 && <rect x={x} y={leftY} width={w} height={leftH} fill="#9775fa" rx={1} />}
            {b.right > 0 && (
              <rect x={x} y={rightY} width={w} height={rightH} fill={COLOR_CORRECT} rx={1} />
            )}
          </g>
        );
      })}
      {Array.from({ length: maxBin / binSize + 1 }, (_, i) => i * binSize).map((tick) => {
        const x = xScale(tick);
        const showLabel = tick % 100 === 0;
        return (
          <g key={`xt-${tick}`}>
            <line x1={x} x2={x} y1={H - PAD_BOTTOM} y2={H - PAD_BOTTOM + 3} stroke="#868e96" />
            {showLabel && (
              <text x={x} y={H - PAD_BOTTOM + 14} fontSize={10} fill="#868e96" textAnchor="middle">
                {tick}
              </text>
            )}
          </g>
        );
      })}
      <text x={W / 2} y={H - PAD_BOTTOM + 30} fontSize={11} fill="#495057" textAnchor="middle">
        {t(`${TR}.histogram.xAxis`)}
      </text>
      <text
        x={12}
        y={PAD_TOP + plotH / 2}
        fontSize={11}
        fill="#495057"
        textAnchor="middle"
        transform={`rotate(-90 12 ${PAD_TOP + plotH / 2})`}
      >
        {t(`${TR}.histogram.yAxis`)}
      </text>
      <g transform={`translate(${W - PAD_X - 100}, ${PAD_TOP + 2})`}>
        <rect width={10} height={10} fill="#9775fa" />
        <text x={14} y={9} fontSize={10} fill="#495057">
          {t(`${TR}.histogram.leftward`, { count: leftCount })}
        </text>
        <rect y={14} width={10} height={10} fill={COLOR_CORRECT} />
        <text x={14} y={23} fontSize={10} fill="#495057">
          {t(`${TR}.histogram.rightward`, { count: rightCount })}
        </text>
      </g>
    </svg>
  );
}

// ─── visualization: gaze trace panel ────────────────────────────────────────

interface GazeTraceData {
  enriched: EnrichedTrial;
  points: { t: number; gaze: number }[];
}

function traceLabelKey(e: EnrichedTrial): string {
  const isLarge = Math.abs(e.peripheralAngleDeg) > 10;
  switch (e.outcome) {
    case "correct":
      return isLarge ? `${TR}.traces.labelCorrectLarge` : `${TR}.traces.labelCorrectSmall`;
    case "error_corrected":
      return isLarge ? `${TR}.traces.labelCorrectedLarge` : `${TR}.traces.labelCorrectedSmall`;
    case "error_uncorrected":
      return isLarge ? `${TR}.traces.labelUncorrectedLarge` : `${TR}.traces.labelUncorrectedSmall`;
    default:
      return `${TR}.traces.labelInvalid`;
  }
}

function buildGazeTracePoints(
  enriched: EnrichedTrial,
  samples: AntiSaccadeSample[],
): { t: number; gaze: number }[] {
  if (enriched.peripheralShownAt == null) return [];
  const t0 = enriched.peripheralShownAt;
  const trialSamples = samples.filter((s) => s.trialIndex === enriched.trial.trialIndex);
  return trialSamples
    .filter((s) => s.timeStamp >= t0 - 0.2 && s.timeStamp <= t0 + 1.2)
    .map((s) => {
      const gx = combinedHorizontalGaze(s);
      return gx != null ? { t: Math.round((s.timeStamp - t0) * 1000), gaze: gx } : null;
    })
    .filter((p): p is { t: number; gaze: number } => p != null);
}

function GazeTracePanel({ data }: { data: GazeTraceData }) {
  const { t } = useTranslation();
  const { enriched, points } = data;
  const label = t(traceLabelKey(enriched), { n: enriched.trial.trialIndex + 1 });
  const W = 360;
  const H = 180;
  const PAD_X = 44;
  const PAD_TOP = 38;
  const PAD_BOTTOM = 50;
  const tMin = -200;
  const tMax = 1200;
  const yMin = -30;
  const yMax = 30;
  const plotW = W - 2 * PAD_X;
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const xScale = (t: number) => PAD_X + ((t - tMin) / (tMax - tMin)) * plotW;
  const yScale = (deg: number) => PAD_TOP + plotH - ((deg - yMin) / (yMax - yMin)) * plotH;
  const yTicks = [-30, -15, 0, 15, 30];
  const xTicks = Array.from({ length: 29 }, (_, i) => -200 + i * 50);

  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${xScale(p.t)},${yScale(p.gaze)}`)
    .join(" ");

  const stimAngle = enriched.peripheralAngleDeg;
  const antiAngle = enriched.antiTargetAngleDeg;
  const stimSideLabel = t(stimAngle > 0 ? `${TR}.traces.sideRight` : `${TR}.traces.sideLeft`);
  const subline = t(`${TR}.traces.subline`, {
    side: stimSideLabel,
    angle: Math.abs(stimAngle).toFixed(1),
    latency: enriched.latencyMs != null ? `${enriched.latencyMs} ms` : t(`${TR}.noData`),
  });
  const isError =
    enriched.outcome === "error_corrected" || enriched.outcome === "error_uncorrected";

  // peripheral phase shading: 0 to 1000 ms
  const shadeX1 = xScale(0);
  const shadeX2 = xScale(1000);

  return (
    <Card withBorder p="sm" radius="md">
      <Stack gap={2} mb="xs">
        <Text fz="sm" fw={600} c={isError ? "red.7" : undefined}>
          {label}
        </Text>
        <Text fz="xs" c="dimmed">
          {subline}
        </Text>
      </Stack>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }}>
        <rect x={shadeX1} y={PAD_TOP} width={shadeX2 - shadeX1} height={plotH} fill="#fff7e6" />
        {yTicks.map((tick) => (
          <g key={`y-${tick}`}>
            <line x1={PAD_X} x2={W - PAD_X} y1={yScale(tick)} y2={yScale(tick)} stroke="#f1f3f5" />
            <text x={PAD_X - 6} y={yScale(tick) + 3} fontSize={10} fill="#868e96" textAnchor="end">
              {tick > 0 ? `+${tick}` : tick}
            </text>
          </g>
        ))}
        {xTicks.map((tick) => {
          const showLabel = tick % 200 === 0;
          return (
            <g key={`x-${tick}`}>
              <line
                x1={xScale(tick)}
                x2={xScale(tick)}
                y1={H - PAD_BOTTOM}
                y2={H - PAD_BOTTOM + (showLabel ? 4 : 2)}
                stroke="#868e96"
              />
              {showLabel && (
                <text
                  x={xScale(tick)}
                  y={H - PAD_BOTTOM + 16}
                  fontSize={10}
                  fill="#868e96"
                  textAnchor="middle"
                >
                  {tick}
                </text>
              )}
            </g>
          );
        })}
        {/* stim onset line */}
        <line
          x1={xScale(0)}
          x2={xScale(0)}
          y1={PAD_TOP}
          y2={H - PAD_BOTTOM}
          stroke="#868e96"
          strokeDasharray="3 2"
        />
        {/* stimulus level */}
        <line
          x1={PAD_X}
          x2={W - PAD_X}
          y1={yScale(stimAngle)}
          y2={yScale(stimAngle)}
          stroke={COLOR_ERROR}
          strokeDasharray="3 2"
          opacity={0.6}
        />
        {/* anti-target level */}
        <line
          x1={PAD_X}
          x2={W - PAD_X}
          y1={yScale(antiAngle)}
          y2={yScale(antiAngle)}
          stroke={COLOR_CORRECT}
          strokeDasharray="3 2"
          opacity={0.6}
        />
        {/* eye position */}
        {pathD && <path d={pathD} fill="none" stroke="#1c7ed6" strokeWidth={1.4} />}
        {/* axis unit labels */}
        <text
          x={PAD_X + plotW / 2}
          y={H - PAD_BOTTOM + 32}
          fontSize={11}
          fill="#495057"
          textAnchor="middle"
        >
          {t(`${TR}.traces.xAxis`)}
        </text>
        <text
          x={10}
          y={PAD_TOP + plotH / 2}
          fontSize={11}
          fill="#495057"
          textAnchor="middle"
          transform={`rotate(-90 10 ${PAD_TOP + plotH / 2})`}
        >
          {t(`${TR}.traces.yAxis`)}
        </text>
      </svg>
    </Card>
  );
}

// ─── small UI bits ──────────────────────────────────────────────────────────

function MetricTile({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <Card withBorder radius="md" p="md">
      <Stack gap={4}>
        <Text fz="xs" fw={600} c="dimmed" tt="uppercase">
          {label}
        </Text>
        <Text fz={28} fw={700} lh={1.1}>
          {value}
        </Text>
        {sublabel && (
          <Text fz="xs" c="dimmed">
            {sublabel}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function LegendDot({
  color,
  label,
  shape = "circle",
}: {
  color: string;
  label: string;
  shape?: "circle" | "square" | "triangle" | "open" | "dash";
}) {
  return (
    <Group gap={6} wrap="nowrap">
      {shape === "square" ? (
        <Box
          style={{
            width: 12,
            height: 12,
            border: `1.5px solid ${color}`,
            background: "transparent",
          }}
        />
      ) : shape === "triangle" ? (
        <svg width={12} height={12}>
          <polygon points="6,1 11,11 1,11" fill={color} />
        </svg>
      ) : shape === "open" ? (
        <Box
          style={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            border: `1.5px solid ${color}`,
            background: "transparent",
          }}
        />
      ) : shape === "dash" ? (
        <Box style={{ width: 12, height: 2, background: color }} />
      ) : (
        <Box style={{ width: 12, height: 12, borderRadius: "50%", background: color }} />
      )}
      <Text fz="xs">{label}</Text>
    </Group>
  );
}

function ZBandSwatch({ band }: { band: { bg: string; fg: string; labelKey: string } }) {
  const { t } = useTranslation();
  return (
    <Group gap={6} wrap="nowrap">
      <Box
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          background: band.bg,
          border: `1px solid ${band.fg}33`,
        }}
      />
      <Text fz="xs" c="dimmed">
        {t(band.labelKey)}
      </Text>
    </Group>
  );
}

// ─── main component ─────────────────────────────────────────────────────────

interface AntiSaccadesTestEntryProps {
  entry: AntiSaccadesTestResult | null;
  header?: React.ReactNode;
  /** Groups this entry's sections so a section-scoped print keeps all of them. */
  printGroup?: string;
}

export default function AntiSaccadesTestEntry({
  entry,
  header,
  printGroup,
}: AntiSaccadesTestEntryProps) {
  const { t } = useTranslation();
  const { prettier } = usePrintableContext();
  const trials = entry?.results?.trials ?? [];
  const samples: AntiSaccadeSample[] = entry?.results?.samples ?? [];

  const enriched = useMemo(() => {
    if (!trials.length) return [] as EnrichedTrial[];
    const samplesByTrial = indexSamplesByTrial(samples);
    return trials.map((t) => deriveEnrichedTrial(t, samplesByTrial));
  }, [trials, samples]);

  const stats = useMemo(() => computeSummary(enriched), [enriched]);
  const interpretation = useMemo(() => interpret(stats), [stats]);

  const samplingInfo = useMemo(() => {
    if (samples.length < 2) return { rateHz: null as number | null, isiMs: null as number | null };
    const isis: number[] = [];
    for (let i = 1; i < samples.length; i++) {
      const dt = samples[i].timeStamp - samples[i - 1].timeStamp;
      if (dt > 0 && dt < 1) isis.push(dt);
    }
    const med = median(isis);
    return {
      rateHz: med != null && med > 0 ? 1 / med : null,
      isiMs: med != null ? med * 1000 : null,
    };
  }, [samples]);

  const validity = useMemo(() => {
    if (!samples.length)
      return { left: null, right: null, combined: null } as {
        left: number | null;
        right: number | null;
        combined: number | null;
      };
    let total = 0;
    let left = 0;
    let right = 0;
    let combined = 0;
    for (const s of samples) {
      total += 1;
      if (s.leftGazeValid !== false && s.leftGazeAngles?.x != null) left += 1;
      if (s.rightGazeValid !== false && s.rightGazeAngles?.x != null) right += 1;
      if (s.combinedGazeValid !== false && combinedHorizontalGaze(s) != null) combined += 1;
    }
    return {
      left: total ? (left / total) * 100 : null,
      right: total ? (right / total) * 100 : null,
      combined: total ? (combined / total) * 100 : null,
    };
  }, [samples]);

  const traces = useMemo(
    () =>
      enriched
        .map((e) => ({
          enriched: e,
          points: buildGazeTracePoints(e, samples),
        }))
        .filter((d) => d.points.length > 0),
    [enriched, samples],
  );

  if (!trials.length) {
    return (
      <Printable.Section printGroup={printGroup}>
        <Stack>
          {header}
          <Box mx={prettier ? 0 : "xl"}>
            <Text size="sm" c="dimmed">
              {t(`${TR}.empty`)}
            </Text>
          </Box>
        </Stack>
      </Printable.Section>
    );
  }

  const NO_DATA = t(`${TR}.noData`);
  const errorRatePct = stats.errorRate != null ? (stats.errorRate * 100).toFixed(1) : NO_DATA;
  const ciLow = stats.errorRateCI != null ? (stats.errorRateCI[0] * 100).toFixed(1) : null;
  const ciHigh = stats.errorRateCI != null ? (stats.errorRateCI[1] * 100).toFixed(1) : null;

  const techCard = (
    <Card withBorder radius="md" p="sm">
      <Group gap="xl" wrap="wrap">
        <Text fz="xs">
          <b>{t(`${TR}.tech.samplingRate`)}</b>{" "}
          {samplingInfo.rateHz != null
            ? t(`${TR}.tech.samplingRateValue`, {
                rate: samplingInfo.rateHz.toFixed(1),
                isi: samplingInfo.isiMs?.toFixed(2),
              })
            : NO_DATA}
        </Text>
        <Text fz="xs">
          <b>{t(`${TR}.tech.validity`)}</b>{" "}
          {validity.left != null && validity.right != null
            ? t(`${TR}.tech.validityValue`, {
                left: validity.left.toFixed(1),
                right: validity.right.toFixed(1),
              })
            : NO_DATA}
        </Text>
        <Text fz="xs">
          <b>{t(`${TR}.tech.device`)}</b> {entry?.device ?? NO_DATA}
        </Text>
        <Text fz="xs">
          <b>{t(`${TR}.tech.build`)}</b> {entry?.version ?? NO_DATA}
        </Text>
      </Group>
    </Card>
  );

  const interpretationCard = (
    <Card
      radius="md"
      p="md"
      style={{
        background: COLOR_BORDERLINE_BG,
        borderLeft: `4px solid ${COLOR_BORDERLINE_BORDER}`,
      }}
    >
      <Stack gap="xs">
        <Text fz="xs" fw={700} tt="uppercase" c="orange.9">
          {t(`${TR}.card.title`)}
        </Text>
        <Title order={3} fw={700}>
          {t(interpretation.headline.key, interpretation.headline.params)}
        </Title>
        <Text fz="sm" c="dark.6">
          {interpretation.description.map((f) => t(f.key, f.params)).join("")}
        </Text>
        <Group gap="xs" mt={4}>
          <Badge
            color={
              interpretation.riskLabel === "normal"
                ? "green"
                : interpretation.riskLabel === "borderline"
                  ? "yellow"
                  : "red"
            }
            variant="light"
            radius="sm"
          >
            {t(`${TR}.risk.${interpretation.riskLabel}`)}
          </Badge>
          <Badge color="gray" variant="light" radius="sm">
            {t(`${TR}.card.trialsBadge`, { count: stats.trialsPresented })}
          </Badge>
        </Group>
      </Stack>
    </Card>
  );

  const metricTiles = (
    <SimpleGrid cols={prettier ? 4 : { base: 2, md: 4 }} spacing="sm">
      <MetricTile
        label={t(`${TR}.metrics.errorRate`)}
        value={errorRatePct === NO_DATA ? NO_DATA : `${errorRatePct}%`}
        sublabel={
          stats.errorRateCI
            ? t(`${TR}.metrics.errorRateSubWithCI`, {
                errors: stats.errorsCorrected + stats.errorsUncorrected,
                validTrials: stats.validTrials,
                ciLow,
                ciHigh,
              })
            : t(`${TR}.metrics.errorRateSubNoCI`, { validTrials: stats.validTrials })
        }
      />
      <MetricTile
        label={t(`${TR}.metrics.meanLatency`)}
        value={stats.meanLatency != null ? `${stats.meanLatency} ms` : NO_DATA}
        sublabel={
          stats.latencySd != null
            ? t(`${TR}.metrics.meanLatencySubWithSd`, { sd: stats.latencySd })
            : t(`${TR}.metrics.meanLatencySub`)
        }
      />
      <MetricTile
        label={t(`${TR}.metrics.correctionRate`)}
        value={
          stats.correctionRate != null ? `${(stats.correctionRate * 100).toFixed(0)}%` : NO_DATA
        }
        sublabel={t(`${TR}.metrics.correctionRateSub`, {
          corrected: stats.errorsCorrected,
          totalErrors: stats.errorsCorrected + stats.errorsUncorrected,
        })}
      />
      <MetricTile
        label={t(`${TR}.metrics.directionalAsymmetry`)}
        value={stats.directionalAsymmetry != null ? `${stats.directionalAsymmetry} ms` : NO_DATA}
        sublabel={
          stats.leftMeanLatency != null && stats.rightMeanLatency != null
            ? t(`${TR}.metrics.directionalAsymmetrySub`, {
                left: stats.leftMeanLatency,
                right: stats.rightMeanLatency,
              })
            : t(`${TR}.metrics.directionalAsymmetrySubFallback`)
        }
      />
    </SimpleGrid>
  );

  const chipSummaryCard = (
    <Card withBorder radius="md" p="md">
      <Stack gap={4} mb="xs">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap={0}>
            <Title order={5}>{t(`${TR}.summary.title`)}</Title>
            <Text fz="xs" c="dimmed">
              {t(`${TR}.summary.helper`)}
            </Text>
          </Stack>
          <Group gap="md" wrap="wrap">
            <LegendDot color={COLOR_CORRECT} label={t(`${TR}.summary.legendCorrect`)} />
            <LegendDot color={COLOR_CORRECTED} label={t(`${TR}.summary.legendCorrected`)} />
            <LegendDot color={COLOR_ERROR} label={t(`${TR}.summary.legendError`)} />
            <LegendDot color={COLOR_INVALID} label={t(`${TR}.summary.legendInvalid`)} />
          </Group>
        </Group>
      </Stack>
      <Group gap={6} wrap="wrap" mt="xs">
        {enriched.map((e) => {
          const sideLabel = e.trial.peripheralSide === "right" ? "R" : "L";
          const bg = outcomeColor(e.outcome);
          const latency = e.latencyMs != null ? `${e.latencyMs}` : NO_DATA;
          return (
            <Stack
              key={`chip-${e.trial.trialIndex}`}
              gap={0}
              align="center"
              style={{
                width: 36,
                paddingTop: 4,
                paddingBottom: 4,
                borderRadius: 4,
                background: bg,
                color: "#fff",
              }}
            >
              <Text fz="sm" fw={700} lh={1.1} style={{ color: "#fff" }}>
                {sideLabel}
              </Text>
              <Text fz={10} lh={1.1} style={{ color: "#fff", opacity: 0.92 }}>
                {latency}
              </Text>
            </Stack>
          );
        })}
      </Group>
    </Card>
  );

  const numericTableCard = (
    <Card withBorder radius="md" p="md">
      <Title order={5} mb="xs">
        {t(`${TR}.numericTable.title`)}
      </Title>
      <Table verticalSpacing="xs" horizontalSpacing="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t(`${TR}.numericTable.measure`)}</Table.Th>
            <Table.Th style={{ textAlign: "right" }}>{t(`${TR}.numericTable.value`)}</Table.Th>
            <Table.Th>{t(`${TR}.numericTable.definition`)}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <NumRow
            label={t(`${TR}.numericTable.trialsPresented`)}
            value={`${stats.trialsPresented}`}
            def={t(`${TR}.numericTable.trialsPresentedDef`)}
          />
          <NumRow
            label={t(`${TR}.numericTable.validTrials`)}
            value={`${stats.validTrials}`}
            def={t(`${TR}.numericTable.validTrialsDef`)}
          />
          <NumRow
            label={t(`${TR}.numericTable.correct`)}
            value={`${stats.correct}`}
            def={t(`${TR}.numericTable.correctDef`)}
          />
          <NumRow
            label={t(`${TR}.numericTable.errorsUncorrected`)}
            value={`${stats.errorsUncorrected}`}
            def={t(`${TR}.numericTable.errorsUncorrectedDef`)}
          />
          <NumRow
            label={t(`${TR}.numericTable.errorsCorrected`)}
            value={`${stats.errorsCorrected}`}
            def={t(`${TR}.numericTable.errorsCorrectedDef`)}
          />
          <NumRow
            label={t(`${TR}.numericTable.expressSaccades`)}
            value={`${stats.expressSaccades}`}
            def={t(`${TR}.numericTable.expressSaccadesDef`, { ms: EXPRESS_LATENCY_MS })}
          />
          <NumRow
            label={t(`${TR}.numericTable.medianLatency`)}
            value={stats.medianLatency != null ? `${stats.medianLatency} ms` : NO_DATA}
            def=""
          />
          <NumRow
            label={t(`${TR}.numericTable.latencySd`)}
            value={stats.latencySd != null ? `${stats.latencySd} ms` : NO_DATA}
            def={t(`${TR}.numericTable.latencySdDef`)}
          />
          <NumRow
            label={t(`${TR}.numericTable.amplitudeGain`)}
            value={stats.meanAmplitudeGain != null ? stats.meanAmplitudeGain.toFixed(2) : NO_DATA}
            def={t(`${TR}.numericTable.amplitudeGainDef`)}
          />
        </Table.Tbody>
      </Table>
    </Card>
  );

  const trialChartCard = (
    <Card withBorder radius="md" p="md">
      <Stack gap={4} mb="sm">
        <Title order={5}>{t(`${TR}.trialByTrial.title`)}</Title>
        <Text fz="xs" c="dimmed">
          {t(`${TR}.trialByTrial.helper`)}
        </Text>
      </Stack>
      <TrialByTrialChart enriched={enriched} />
      <Group gap="lg" mt="sm" wrap="wrap">
        <LegendDot color={COLOR_CORRECT} label={t(`${TR}.trialByTrial.legendCorrect`)} />
        <LegendDot color={COLOR_ERROR} label={t(`${TR}.trialByTrial.legendErrorUncorrected`)} />
        <LegendDot color={COLOR_CORRECTED} label={t(`${TR}.trialByTrial.legendCorrected`)} />
        <LegendDot
          color={COLOR_INVALID}
          label={t(`${TR}.trialByTrial.legendInvalid`)}
          shape="dash"
        />
        <LegendDot
          color={COLOR_STIMULUS}
          label={t(`${TR}.trialByTrial.legendStimulus`)}
          shape="square"
        />
        <LegendDot
          color={COLOR_ANTITARGET}
          label={t(`${TR}.trialByTrial.legendAntiTarget`)}
          shape="triangle"
        />
      </Group>
      <Group gap="md" mt={6} wrap="wrap">
        <Text fz="xs" c="dimmed" fw={600}>
          {t(`${TR}.trialByTrial.zBandLabel`)}
        </Text>
        <ZBandSwatch band={ZBAND_FAST} />
        <ZBandSwatch band={ZBAND_TYPICAL} />
        <ZBandSwatch band={ZBAND_MILD} />
        <ZBandSwatch band={ZBAND_MODERATE} />
        <ZBandSwatch band={ZBAND_HIGH} />
      </Group>
      <Text fz="xs" c="dimmed" mt={4}>
        {t(`${TR}.trialByTrial.normNote`, {
          mean: NORM_LATENCY_MEAN_MS,
          sd: NORM_LATENCY_SD_MS,
        })}
      </Text>
    </Card>
  );

  const distributionsGrid = (
    <SimpleGrid cols={prettier ? 2 : { base: 1, md: 2 }} spacing="md">
      <Card withBorder radius="md" p="md">
        <Title order={5} mb={4}>
          {t(`${TR}.endpoints.title`)}
        </Title>
        <Text fz="xs" c="dimmed" mb="xs">
          {t(`${TR}.endpoints.helper`)}
        </Text>
        <EndpointDistribution enriched={enriched} />
        <Group gap="md" mt="xs" wrap="wrap">
          <LegendDot color={COLOR_CORRECT} label={t(`${TR}.endpoints.legendCorrect`)} />
          <LegendDot color={COLOR_CORRECTED} label={t(`${TR}.endpoints.legendCorrected`)} />
          <LegendDot color={COLOR_ERROR} label={t(`${TR}.endpoints.legendError`)} />
        </Group>
      </Card>
      <Card withBorder radius="md" p="md">
        <Title order={5} mb={4}>
          {t(`${TR}.histogram.title`)}
        </Title>
        <Text fz="xs" c="dimmed" mb="xs">
          {t(`${TR}.histogram.helper`, { expressMs: EXPRESS_LATENCY_MS })}
        </Text>
        <LatencyHistogram enriched={enriched} />
      </Card>
    </SimpleGrid>
  );

  const tracesTitle = (
    <Stack gap={4} mb="sm">
      <Title order={5}>{t(`${TR}.traces.title`)}</Title>
      <Text fz="xs" c="dimmed">
        {t(`${TR}.traces.helper`)}
      </Text>
      <Group gap="md" wrap="wrap" mt={2}>
        <LegendDot color="#868e96" label={t(`${TR}.traces.stimOn`)} shape="dash" />
      </Group>
    </Stack>
  );

  const disclaimer = (
    <Text fz="xs" c="dimmed">
      {t(`${TR}.disclaimer`)}
    </Text>
  );

  if (!prettier) {
    return (
      <Printable.Section printGroup={printGroup}>
        <Stack>
          {header}
          <Box mx="xl">
            <Stack gap="md">
              {techCard}
              {interpretationCard}
              {metricTiles}
              {chipSummaryCard}
              {numericTableCard}
              {trialChartCard}
              {distributionsGrid}
              {traces.length > 0 && (
                <Card withBorder radius="md" p="md">
                  {tracesTitle}
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
                    {traces.map((d) => (
                      <GazeTracePanel key={d.enriched.trial.trialIndex} data={d} />
                    ))}
                  </SimpleGrid>
                </Card>
              )}
              {disclaimer}
            </Stack>
          </Box>
        </Stack>
      </Printable.Section>
    );
  }

  // Bordered report layout (test suite overviews): one A4 page per section so
  // page breaks never slice through a chart. The trace panels are chunked into
  // page-sized groups for the same reason.
  const traceChunks: GazeTraceData[][] = [];
  for (let i = 0; i < traces.length; i += TRACE_PANELS_PER_PRINT_PAGE) {
    traceChunks.push(traces.slice(i, i + TRACE_PANELS_PER_PRINT_PAGE));
  }

  return (
    <>
      <Printable.Section printGroup={printGroup}>
        <Stack gap="md">
          {header}
          {techCard}
          {interpretationCard}
          {metricTiles}
          {chipSummaryCard}
        </Stack>
      </Printable.Section>
      <Printable.Section printGroup={printGroup}>
        <Stack gap="md">
          {numericTableCard}
          {distributionsGrid}
        </Stack>
      </Printable.Section>
      <Printable.Section printGroup={printGroup}>
        <Stack gap="md">
          {trialChartCard}
          {traceChunks.length === 0 && disclaimer}
        </Stack>
      </Printable.Section>
      {traceChunks.map((chunk, chunkIndex) => (
        <Printable.Section
          key={`traces-${chunk[0].enriched.trial.trialIndex}`}
          printGroup={printGroup}
        >
          <Stack gap="md">
            <Card withBorder radius="md" p="md">
              {chunkIndex === 0 && tracesTitle}
              <SimpleGrid cols={3} spacing="sm">
                {chunk.map((d) => (
                  <GazeTracePanel key={d.enriched.trial.trialIndex} data={d} />
                ))}
              </SimpleGrid>
            </Card>
            {chunkIndex === traceChunks.length - 1 && disclaimer}
          </Stack>
        </Printable.Section>
      ))}
    </>
  );
}

function NumRow({ label, value, def }: { label: string; value: string; def: string }) {
  return (
    <Table.Tr>
      <Table.Td>{label}</Table.Td>
      <Table.Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Table.Td>
      <Table.Td>
        <Text fz="xs" c="dimmed">
          {def}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}
